import purify from 'dompurify';
import Quill from 'quill';
import { useEffect, useState } from 'react';
import { Box } from '@act/storybook';

const quillClassStartPattern = 'ql-';
const quillClassAfterOtherClassPattern = `(\\w|\\s|_|-)*\\s${quillClassStartPattern}`;
const hasQuillClassRegex = new RegExp(
  `\\sclass="(${quillClassStartPattern}|${quillClassAfterOtherClassPattern})`
);

type HtmlDisplayerWordWrap = 'break-word' | 'normal';

interface HtmlDisplayerProps {
  innerHtml: string;
  className?: string;
  wordWrap?: HtmlDisplayerWordWrap;
}

const getSemanticHTMLFromQuillHTML = async (html: string) => {
  const container = document.createElement('div');
  const quill = new Quill(container);
  quill.root.innerHTML = html;

  // Wait for the quill to update the DOM
  await new Promise(resolve => setTimeout(() => resolve(null), 0));

  const semanticHtml = quill.getSemanticHTML();
  return purify.sanitize(semanticHtml);
};

const getCleanHtml = async (html: string) => {
  const isQuillHtml = hasQuillClassRegex.test(html);
  if (isQuillHtml) {
    html = await getSemanticHTMLFromQuillHTML(html);
  }
  return purify.sanitize(html);
};

/**
 * Sanitizes and renders trusted rich text HTML.
 *
 * @param props - HTML content and display options.
 * @returns Sanitized HTML content.
 */
const HtmlDisplayer = ({
  innerHtml,
  className,
  wordWrap = 'break-word',
}: HtmlDisplayerProps) => {
  const [cleanHtml, setCleanHtml] = useState('');

  useEffect(() => {
    getCleanHtml(innerHtml).then(cleanHtml => setCleanHtml(cleanHtml));
  }, [innerHtml]);

  return (
    <Box
      overflowWrap={wordWrap}
      wordWrap={wordWrap}
      wordBreak="normal"
      whiteSpace="normal"
      width="100%"
      maxWidth="100%"
      boxSizing="border-box"
      paddingRight={wordWrap === 'normal' ? '4px' : undefined}
    >
      <div
        className={className}
        dangerouslySetInnerHTML={{ __html: cleanHtml }}
      />
    </Box>
  );
};

export default HtmlDisplayer;
